/**
 * URL utility functions for consistent URL handling across the application
 */

/**
 * Normalizes a URL to ensure it has a proper protocol
 * @param url - The URL to normalize
 * @returns The normalized URL with proper protocol
 */
export const normalizeUrl = (url: string): string => {
  if (!url || url.trim() === '') return '';
  
  const trimmedUrl = url.trim();
  
  // If URL already has a protocol, return as is
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }
  
  // If URL starts with //, add https:
  if (trimmedUrl.startsWith('//')) {
    return `https:${trimmedUrl}`;
  }
  
  // For all other cases, add https://
  return `https://${trimmedUrl}`;
};

/**
 * Encodes unsafe characters (e.g. spaces) so the full value stays one valid href.
 * Use for <a href> / window.open only — not for persisted meeting URLs or visible text.
 */
export const toSafeHref = (url: string): string => {
  const normalized = normalizeUrl(url);
  if (!normalized) return '';

  try {
    return new URL(normalized).href;
  } catch {
    try {
      return new URL(encodeURI(normalized)).href;
    } catch {
      return encodeURI(normalized);
    }
  }
};

/**
 * Persistable meeting URL: protocol-normalized only.
 * Does not convert spaces to %20 (that looked wrong in UI/GCal). Reject spaces via validation before save.
 */
export const sanitizeMeetingUrl = (url: string): string => normalizeUrl(url);

/**
 * Human-readable meeting URL for UI (decode %20 from legacy stored links).
 */
export const formatMeetingUrlForDisplay = (url: string): string => {
  if (!url || url.trim() === '') return '';
  try {
    return decodeURI(url.trim());
  } catch {
    return url.trim();
  }
};

const MEETING_URL_NO_SPACES_MESSAGE =
  'Meeting link cannot contain spaces. Please enter a valid URL without spaces.';

/**
 * Shared meeting-link validation. Rejects whitespace so we never store/display %20 encodings.
 */
export const getMeetingUrlValidationError = (
  url: string,
  options?: { required?: boolean; httpsOnly?: boolean }
): string | null => {
  const required = options?.required !== false;
  const httpsOnly = options?.httpsOnly === true;
  const trimmed = (url || '').trim();

  if (!trimmed) {
    return required ? 'Meeting link is required.' : null;
  }

  if (/\s/.test(trimmed)) {
    return MEETING_URL_NO_SPACES_MESSAGE;
  }

  const urlPattern = httpsOnly
    ? /^(https:\/\/)([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*(\?[^#]*)?(#.*)?$/i
    : /^(https?:\/\/)([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*(\?[^#]*)?(#.*)?$/i;

  if (!urlPattern.test(trimmed)) {
    return httpsOnly
      ? 'Please enter a valid HTTPS URL (e.g., https://example.com)'
      : 'Please enter a valid URL (e.g., https://example.com)';
  }

  try {
    const parsed = new URL(trimmed);
    if (httpsOnly && parsed.protocol !== 'https:') {
      return 'Please enter a valid HTTPS URL (e.g., https://example.com)';
    }
  } catch {
    return httpsOnly
      ? 'Please enter a valid HTTPS URL (e.g., https://example.com)'
      : 'Please enter a valid URL (e.g., https://example.com)';
  }

  return null;
};

/**
 * Validates if a URL is properly formatted
 * @param url - The URL to validate (must include protocol http:// or https://)
 * @returns True if the URL is valid, false otherwise
 */
export const isValidUrl = (url: string): boolean => {
  if (!url || url.trim() === '') return false;
  // Spaces are not valid in meeting/join URLs we accept
  if (/\s/.test(url.trim())) return false;
  
  try {
    const urlPattern = /^(https?:\/\/)([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*(\?[^#]*)?(#.*)?$/;
    
    if (!urlPattern.test(url)) {
      return false;
    }
    
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Extracts the domain from a URL
 * @param url - The URL to extract domain from
 * @returns The domain name or empty string if invalid
 */
export const extractDomain = (url: string): string => {
  if (!url || url.trim() === '') return '';
  
  try {
    const normalizedUrl = toSafeHref(url);
    const urlObj = new URL(normalizedUrl);
    return urlObj.hostname;
  } catch {
    return '';
  }
};
